import React, { useState, useEffect, useCallback, useMemo } from "react";
import KernteamLayout from '../../KernteamLayout';
import BackArrow from '../../BackArrow';
import { host_url } from '../../App';
import ImportModal from '../ImportModal';
import LogistikConfirm from '../LogistikConfirm';
import "../logistik-base.css";
import "./Fahrten.css";

const IMPORT_COLUMNS = [
  { key: 'Tag',        label: 'Tag',         required: true,
    parse: v => v.includes('.') ? umkehrenDatum(v) : v },
  { key: 'Reihenfolge', label: 'Nr',         required: false,
    parse: v => parseInt(v) || 1 },
  { key: 'Fahrzeug',  label: 'Fahrzeug',     required: false },
  { key: 'Fahrer',    label: 'Fahrer',       required: false },
  { key: 'Abfahrt',   label: 'Abfahrt',      required: false },
  { key: 'Ziel',      label: 'Ziel',         required: false },
  { key: 'Anschrift', label: 'Anschrift_AP', required: false },
  { key: 'Infos',     label: 'Infos',        required: false },
];

function umkehrenDatum(s) {
  const parts = s.split('.');
  if (parts.length === 3) return `${parts[2].padStart(4,'20')}-${parts[1].padStart(2,'0')}-${parts[0].padStart(2,'0')}`;
  return s;
}

const TEMPLATE_ROWS = [
  ['2026-07-06', 1, 'Blauer Bus (OD)', 'Nils',     '08:00',  'Oststeinbek',      'Musterstr. 12',  'Zelte aufbauen'],
  ['2026-07-06', 2, 'LKW',            'Thorsten', '10:00',  'Stadttor',          '',               'Material von Lager'],
  ['2026-07-07', 1, 'Weißer Bus (JGH)', 'Hannes', '07:30',  'Bahnhof',           '',               'Ankömmlinge abholen'],
];

const CAMP_TAGE = [
  { datum: '2026-07-01', label: 'Mi 01.07.' },
  { datum: '2026-07-02', label: 'Do 02.07.' },
  { datum: '2026-07-03', label: 'Fr 03.07.' },
  { datum: '2026-07-04', label: 'Sa 04.07.' },
  { datum: '2026-07-05', label: 'So 05.07.' },
  { datum: '2026-07-06', label: 'Mo 06.07.' },
  { datum: '2026-07-07', label: 'Di 07.07.' },
  { datum: '2026-07-08', label: 'Mi 08.07.' },
  { datum: '2026-07-09', label: 'Do 09.07.' },
  { datum: '2026-07-10', label: 'Fr 10.07.' },
  { datum: '2026-07-11', label: 'Sa 11.07.' },
];

const FAHRZEUGE = [
  '',
  'Blauer Bus (OD)',
  'Weißer Bus (JGH)',
  'Roter Bus',
  'JAW',
  'LKW',
  'Kjells Auto',
  'Privat',
  'DLRG',
  'Neuer Bus',
];

const LEER = { Reihenfolge: 1, Fahrzeug: '', Fahrer: '', Abfahrt: '', Anschrift: '', Ziel: '', Infos: '' };

function tagAlsString(tag) {
  if (!tag) return '';
  if (typeof tag === 'string') return tag.substring(0, 10);
  return new Date(tag).toISOString().substring(0, 10);
}

function heutigesDatum() {
  const heute = new Date();
  const jahr = heute.getFullYear();
  const monat = String(heute.getMonth() + 1).padStart(2, '0');
  const tag = String(heute.getDate()).padStart(2, '0');
  return `${jahr}-${monat}-${tag}`;
}

function aktuellerCampTag(fallback = CAMP_TAGE[5].datum) {
  const heute = heutigesDatum();
  return CAMP_TAGE.some(tag => tag.datum === heute) ? heute : fallback;
}

function normalisiereText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function sortiereFahrten(a, b) {
  const reihenfolgeA = Number(a.Reihenfolge) || 9999;
  const reihenfolgeB = Number(b.Reihenfolge) || 9999;
  if (reihenfolgeA !== reihenfolgeB) return reihenfolgeA - reihenfolgeB;
  if ((a.Abfahrt || '') !== (b.Abfahrt || '')) return String(a.Abfahrt || '').localeCompare(String(b.Abfahrt || ''));
  return String(a.Ziel || '').localeCompare(String(b.Ziel || ''), 'de');
}

function fahrtenText(fahrt) {
  return normalisiereText([
    fahrt.Fahrzeug,
    fahrt.Fahrer,
    fahrt.Abfahrt,
    fahrt.Ziel,
    fahrt.Anschrift,
    fahrt.Infos,
  ].join(' '));
}

function eindeutigeWerte(fahrten, feld) {
  return new Set(fahrten.map(fahrt => String(fahrt[feld] || '').trim()).filter(Boolean)).size;
}

function naechsteReihenfolge(fahrten) {
  return fahrten.reduce((max, fahrt) => Math.max(max, Number(fahrt.Reihenfolge) || 0), 0) + 1;
}

function istFahrtLeer(form) {
  return !['Fahrzeug', 'Fahrer', 'Abfahrt', 'Anschrift', 'Ziel', 'Infos']
    .some(key => String(form[key] || '').trim());
}

async function parseResponseError(res) {
  const text = await res.text();
  try {
    const data = JSON.parse(text);
    return data.message || text;
  } catch {
    return text || `HTTP ${res.status}`;
  }
}

function Fahrten() {
  const [selectedTag, setSelectedTag] = useState(() => aktuellerCampTag());
  const [alleFahrten, setAlleFahrten] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(LEER);
  const [addForm, setAddForm] = useState(LEER);
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [suchtext, setSuchtext] = useState('');
  const [laedt, setLaedt] = useState(false);
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState('');
  const [meldung, setMeldung] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const ladeFahrten = useCallback(() => {
    setLaedt(true);
    setFehler('');
    fetch(`${host_url}/logistik/fahrten`)
      .then(async r => {
        if (!r.ok) throw new Error(await parseResponseError(r));
        return r.json();
      })
      .then(data => setAlleFahrten(Array.isArray(data) ? data : []))
      .catch(err => {
        console.error(err);
        setFehler(`Fahrten konnten nicht geladen werden: ${err.message}`);
      })
      .finally(() => setLaedt(false));
  }, []);

  useEffect(() => { ladeFahrten(); }, [ladeFahrten]);

  const fahrtenFuerTag = useMemo(() => alleFahrten
    .filter(f => tagAlsString(f.Tag) === selectedTag)
    .sort(sortiereFahrten), [alleFahrten, selectedTag]);

  const query = normalisiereText(suchtext);
  const gefilterteFahrten = useMemo(() => (
    query ? fahrtenFuerTag.filter(fahrt => fahrtenText(fahrt).includes(query)) : fahrtenFuerTag
  ), [fahrtenFuerTag, query]);

  const fahrtenProTag = useMemo(() => {
    const counts = {};
    alleFahrten.forEach(fahrt => {
      const tag = tagAlsString(fahrt.Tag);
      counts[tag] = (counts[tag] || 0) + 1;
    });
    return counts;
  }, [alleFahrten]);

  const tagesStats = useMemo(() => ({
    fahrten: fahrtenFuerTag.length,
    fahrzeuge: eindeutigeWerte(fahrtenFuerTag, 'Fahrzeug'),
    fahrer: eindeutigeWerte(fahrtenFuerTag, 'Fahrer'),
  }), [fahrtenFuerTag]);

  async function sendeFahrt(url, method, payload, erfolg) {
    setSpeichert(true);
    setFehler('');
    setMeldung('');
    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await parseResponseError(res));
      setMeldung(erfolg);
      await ladeFahrten();
      return true;
    } catch (err) {
      console.error(err);
      setFehler(err.message || 'Speichern fehlgeschlagen.');
      return false;
    } finally {
      setSpeichert(false);
    }
  }

  async function handleAdd() {
    if (istFahrtLeer(addForm)) {
      setFehler('Bitte mindestens Fahrzeug, Fahrer, Abfahrt, Ziel oder Info eintragen.');
      return;
    }
    const success = await sendeFahrt(
      `${host_url}/logistik/fahrten`,
      'POST',
      { ...addForm, Tag: selectedTag, Reihenfolge: Number(addForm.Reihenfolge) || naechsteReihenfolge(fahrtenFuerTag) },
      'Fahrt wurde hinzugefügt.'
    );
    if (success) {
      setAddForm({ ...LEER, Reihenfolge: naechsteReihenfolge(fahrtenFuerTag) + 1 });
      setShowAdd(false);
    }
  }

  async function handleEditSave(id) {
    if (istFahrtLeer(editForm)) {
      setFehler('Bitte mindestens Fahrzeug, Fahrer, Abfahrt, Ziel oder Info eintragen.');
      return;
    }
    const success = await sendeFahrt(
      `${host_url}/logistik/fahrten/${id}`,
      'PUT',
      { ...editForm, Tag: selectedTag, Reihenfolge: Number(editForm.Reihenfolge) || 1 },
      'Fahrt wurde gespeichert.'
    );
    if (success) setEditId(null);
  }

  async function confirmDelete() {
    if (!deleteConfirm) return;
    setSpeichert(true);
    setFehler('');
    setMeldung('');
    try {
      const res = await fetch(`${host_url}/logistik/fahrten/${deleteConfirm.ID}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(await parseResponseError(res));
      setMeldung('Fahrt wurde gelöscht.');
      setDeleteConfirm(null);
      await ladeFahrten();
    } catch (err) {
      console.error(err);
      setFehler(err.message || 'Löschen fehlgeschlagen.');
    } finally {
      setSpeichert(false);
    }
  }

  async function handleMove(idx, dir) {
    if (idx < 0) return;
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= fahrtenFuerTag.length) return;
    const neuSortiert = [...fahrtenFuerTag];
    const [bewegt] = neuSortiert.splice(idx, 1);
    neuSortiert.splice(swapIdx, 0, bewegt);
    setSpeichert(true);
    setFehler('');
    setMeldung('');
    try {
      await Promise.all(neuSortiert.map((fahrt, index) => {
        const neueReihenfolge = index + 1;
        if (Number(fahrt.Reihenfolge) === neueReihenfolge) return Promise.resolve();
        return fetch(`${host_url}/logistik/fahrten/${fahrt.ID}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...fahrt, Reihenfolge: neueReihenfolge, Tag: tagAlsString(fahrt.Tag) }),
        }).then(async res => {
          if (!res.ok) throw new Error(await parseResponseError(res));
        });
      }));
      setMeldung('Reihenfolge wurde aktualisiert.');
      await ladeFahrten();
    } catch (err) {
      console.error(err);
      setFehler(err.message || 'Reihenfolge konnte nicht gespeichert werden.');
    } finally {
      setSpeichert(false);
    }
  }

  function startEdit(f) {
    setShowAdd(false);
    setEditId(f.ID);
    setEditForm({
      Reihenfolge: f.Reihenfolge,
      Fahrzeug: f.Fahrzeug || '',
      Fahrer: f.Fahrer || '',
      Abfahrt: f.Abfahrt || '',
      Anschrift: f.Anschrift || '',
      Ziel: f.Ziel || '',
      Infos: f.Infos || '',
      Tag: tagAlsString(f.Tag),
    });
  }

  function waehleTag(tag) {
    setSelectedTag(tag);
    setShowAdd(false);
    setEditId(null);
    setMeldung('');
  }

  const tagLabel = CAMP_TAGE.find(t => t.datum === selectedTag)?.label || selectedTag;

  return (
    <KernteamLayout>
      <div className="container fahrten-container">
        <BackArrow to="/logistik" />
        <h1 className="section-title teal">Fahrten</h1>

        <div className="logistik-tabs no-print">
          {CAMP_TAGE.map(t => (
            <button
              key={t.datum}
              className={`logistik-tab${selectedTag === t.datum ? ' active' : ''}`}
              onClick={() => waehleTag(t.datum)}
            >
              {t.label}
              {fahrtenProTag[t.datum] > 0 && <span className="logistik-tab-count">{fahrtenProTag[t.datum]}</span>}
            </button>
          ))}
        </div>

        <div className="logistik-toolbar no-print">
          <div className="logistik-search-field">
            <label htmlFor="fahrten-suche">Fahrten durchsuchen</label>
            <input
              id="fahrten-suche"
              className="logistik-input"
              value={suchtext}
              placeholder="Fahrer, Fahrzeug, Ziel, Adresse, Info"
              onChange={e => setSuchtext(e.target.value)}
            />
          </div>
          <div className="logistik-stat-strip" aria-label="Tagesübersicht">
            <span className="logistik-stat-pill">{tagesStats.fahrten} Fahrten</span>
            <span className="logistik-stat-pill">{tagesStats.fahrzeuge} Fahrzeuge</span>
            <span className="logistik-stat-pill">{tagesStats.fahrer} Fahrer</span>
          </div>
        </div>

        {laedt && <div className="logistik-state-box no-print">Fahrten werden geladen...</div>}
        {speichert && <div className="logistik-state-box no-print">Änderung wird gespeichert...</div>}
        {meldung && <div className="logistik-state-box no-print">{meldung}</div>}
        {fehler && <div className="logistik-state-box error no-print">{fehler}</div>}

        <div className="print-day-title only-print">Fahrtenliste — {tagLabel}</div>

        {gefilterteFahrten.length === 0 && !showAdd ? (
          <div className="logistik-empty">
            {fahrtenFuerTag.length === 0 ? 'Keine Fahrten für diesen Tag.' : 'Keine Fahrten zur Suche gefunden.'}
          </div>
        ) : (
          <div className="fahrten-table-wrapper">
            <table className="fahrten-table">
              <thead>
                <tr>
                  <th className="col-nr">Nr.</th>
                  <th>Fahrzeug</th>
                  <th>Fahrer</th>
                  <th>Abfahrt</th>
                  <th>Ziel</th>
                  <th>Anschrift / AP</th>
                  <th>Nähere Infos</th>
                  <th className="no-print col-order"></th>
                  <th className="no-print col-actions"></th>
                </tr>
              </thead>
              <tbody>
                {gefilterteFahrten.map((f) => {
                  const sortIdx = fahrtenFuerTag.findIndex(fahrt => fahrt.ID === f.ID);
                  return (
                    <React.Fragment key={f.ID}>
                      {editId === f.ID ? (
                        <tr className="fahrt-edit-row">
                          <td colSpan="9">
                            <FahrtForm
                              form={editForm}
                              setForm={setEditForm}
                              onSave={() => handleEditSave(f.ID)}
                              onCancel={() => setEditId(null)}
                              disabled={speichert}
                            />
                          </td>
                        </tr>
                      ) : (
                        <tr className="fahrt-row">
                          <td className="col-nr" data-label="Nr.">{f.Reihenfolge}</td>
                          <td data-label="Fahrzeug">{f.Fahrzeug || '-'}</td>
                          <td data-label="Fahrer">{f.Fahrer || '-'}</td>
                          <td className="col-abfahrt" data-label="Abfahrt">{f.Abfahrt || '-'}</td>
                          <td data-label="Ziel">{f.Ziel || '-'}</td>
                          <td className="col-adresse" data-label="Anschrift / AP">{f.Anschrift || '-'}</td>
                          <td className="col-infos" data-label="Infos">{f.Infos || ''}</td>
                          <td className="no-print col-order">
                            <button className="order-btn" onClick={() => handleMove(sortIdx, -1)} disabled={speichert || Boolean(query) || sortIdx <= 0}>↑</button>
                            <button className="order-btn" onClick={() => handleMove(sortIdx, 1)} disabled={speichert || Boolean(query) || sortIdx === fahrtenFuerTag.length - 1}>↓</button>
                          </td>
                          <td className="no-print col-actions">
                            <div className="logistik-actions">
                              <button className="logistik-btn logistik-edit-btn" onClick={() => startEdit(f)} disabled={speichert}>
                                <span className="material-symbols-outlined">edit</span>
                              </button>
                              <button className="logistik-btn logistik-delete-btn" onClick={() => setDeleteConfirm(f)} disabled={speichert}>
                                <span className="material-symbols-outlined">delete</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {showAdd && (
                  <tr className="fahrt-edit-row">
                    <td colSpan="9">
                      <FahrtForm
                        form={addForm}
                        setForm={setAddForm}
                        onSave={handleAdd}
                        onCancel={() => { setShowAdd(false); setAddForm(LEER); }}
                        disabled={speichert}
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="logistik-footer no-print">
          {!showAdd && editId === null && (
            <button
              className="logistik-add-btn"
              onClick={() => { setShowAdd(true); setEditId(null); setAddForm({ ...LEER, Reihenfolge: naechsteReihenfolge(fahrtenFuerTag) }); }}
              disabled={speichert}
            >
              <span className="material-symbols-outlined">add</span>
              Fahrt hinzufügen
            </button>
          )}
          <button className="logistik-import-btn" onClick={() => setShowImport(true)} disabled={speichert}>
            <span className="material-symbols-outlined">upload</span>
            Excel importieren
          </button>
          <button className="logistik-print-link" onClick={() => window.print()}>
            <span className="material-symbols-outlined">print</span>
            <span className="print-text">Drucken</span>
          </button>
        </div>

        {showImport && (
          <ImportModal
            title="Fahrten importieren"
            columns={IMPORT_COLUMNS}
            templateName="vorlage_fahrten"
            templateRows={TEMPLATE_ROWS}
            replaceLabel="Bestehende Fahrten für die betroffenen Tage vorher löschen"
            onImport={async (rows, ersetzen) => {
              const tage = [...new Set(rows.map(r => r.Tag).filter(Boolean))];
              const res = await fetch(`${host_url}/logistik/fahrten/import`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ eintraege: rows, ersetzen, tage }),
              });
              if (!res.ok) throw new Error(await res.text());
              ladeFahrten();
            }}
            onClose={() => setShowImport(false)}
          />
        )}

        {deleteConfirm && (
          <LogistikConfirm
            title="Fahrt löschen?"
            onCancel={() => setDeleteConfirm(null)}
            onConfirm={confirmDelete}
          >
            <p>
              {deleteConfirm.Reihenfolge ? `Nr. ${deleteConfirm.Reihenfolge}: ` : ''}
              {deleteConfirm.Ziel || deleteConfirm.Fahrzeug || 'Diese Fahrt'} wird dauerhaft gelöscht.
            </p>
          </LogistikConfirm>
        )}
      </div>
    </KernteamLayout>
  );
}

function FahrtForm({ form, setForm, onSave, onCancel, disabled = false }) {
  return (
    <div className="fahrt-form">
      <div className="fahrt-form-grid">
        <div className="fahrt-field">
          <label>Nr.</label>
          <input
            className="logistik-input fahrt-nr-input"
            type="number" min="1"
            value={form.Reihenfolge}
            disabled={disabled}
            onChange={e => setForm(f => ({ ...f, Reihenfolge: parseInt(e.target.value) || 1 }))}
          />
        </div>
        <div className="fahrt-field">
          <label>Fahrzeug</label>
          <select
            className="logistik-input"
            value={form.Fahrzeug}
            disabled={disabled}
            onChange={e => setForm(f => ({ ...f, Fahrzeug: e.target.value }))}
          >
            {FAHRZEUGE.map(v => (
              <option key={v} value={v}>{v || '– Fahrzeug wählen –'}</option>
            ))}
          </select>
        </div>
        <div className="fahrt-field">
          <label>Fahrer</label>
          <input
            className="logistik-input"
            placeholder="Fahrer"
            value={form.Fahrer}
            disabled={disabled}
            onChange={e => setForm(f => ({ ...f, Fahrer: e.target.value }))}
          />
        </div>
        <div className="fahrt-field">
          <label>Abfahrt</label>
          <input
            className="logistik-input"
            placeholder="z.B. 08:00 oder Lager"
            value={form.Abfahrt}
            disabled={disabled}
            onChange={e => setForm(f => ({ ...f, Abfahrt: e.target.value }))}
          />
        </div>
        <div className="fahrt-field">
          <label>Ziel</label>
          <input
            className="logistik-input"
            placeholder="Ziel"
            value={form.Ziel}
            disabled={disabled}
            onChange={e => setForm(f => ({ ...f, Ziel: e.target.value }))}
          />
        </div>
        <div className="fahrt-field">
          <label>Anschrift / AP</label>
          <input
            className="logistik-input"
            placeholder="Adresse oder Ansprechpartner"
            value={form.Anschrift}
            disabled={disabled}
            onChange={e => setForm(f => ({ ...f, Anschrift: e.target.value }))}
          />
        </div>
        <div className="fahrt-field span-2">
          <label>Nähere Infos</label>
          <textarea
            className="logistik-input"
            placeholder="Weitere Informationen, Material, Einkaufsliste …"
            value={form.Infos}
            rows={2}
            disabled={disabled}
            onChange={e => setForm(f => ({ ...f, Infos: e.target.value }))}
          />
        </div>
      </div>
      <div className="logistik-actions fahrt-form-actions">
        <button className="logistik-btn logistik-save-btn" onClick={onSave} disabled={disabled}>Speichern</button>
        <button className="logistik-btn logistik-cancel-btn" onClick={onCancel} disabled={disabled}>Abbrechen</button>
      </div>
    </div>
  );
}

export default Fahrten;
